/**
 * CampaignsList — faithful port of the canonical campaigns screen.
 * Tabs: All · Drafts · Scheduled · Sent · Awaiting approval · Automations
 * Grid and table views. Approval badge. Per-campaign actions.
 */
import { useState, useEffect } from "react";
import { type Tone, StatusPill, alpha, Btn, Card, Icon, Input, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { EmptyState, ExplainerFooter, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, CardActionBar } from "../../shell/list-composites";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { listCampaigns, deleteCampaign, duplicateCampaign, sendCampaign, cancelCampaign, approveCampaign, rejectCampaign, exportCampaign } from "@v0y4ge/api-client";
import { useT, t as i18nT } from "../../lib/i18n";
import { friendlyDateTime, useDateTimeVersion } from "../../lib/datetime";
import { APPROVALS_COMING_SOON } from "../approvals/approvals-store";

/* ── Fictional campaign seed ──────────────────────────────────────── */
interface Campaign {
  id: string; name: string; type: "Email"|"SMS";
  audience: string; status: "draft"|"scheduled"|"sent";
  approval?: "pending"|"approved"|"rejected";
  sentAt?: string; scheduledAt?: string;
  opens?: number; clicks?: number; deliveries?: number;
  isAutomation?: boolean;
}


const thStyle = { padding:"10px 16px", textAlign:"left" as const, fontWeight:600, fontSize:11, textTransform:"uppercase" as const, color:T.soft, whiteSpace:"nowrap" as const };
const tdStyle = { padding:"12px 16px", verticalAlign:"middle" as const };

/* Status → canonical tone. Configuration, not data. */
const CAMPAIGN_TONE: Record<string, Tone> = { sent: "success", scheduled: "info" };
function CampaignStatus({ c }: { c: Campaign }) {
  const t = useT();
  const label = c.status === "sent" ? t("campaigns.list.sent", "Sent") : c.status === "scheduled" ? t("common.status.scheduled", "Scheduled") : t("common.status.draft", "Draft");
  return <StatusPill tone={CAMPAIGN_TONE[c.status] ?? "neutral"}>{label}</StatusPill>;
}
function ApprovalPill({ c }: { c: Campaign }) {
  const t = useT();
  if (APPROVALS_COMING_SOON || !c.approval) return null;
  if (c.approval==="pending")  return <Pill tone="warn" dot>{t("campaigns.list.approvalPending", "Approval pending")}</Pill>;
  if (c.approval==="approved") return <Pill tone="success">{t("campaigns.list.approved", "Approved")}</Pill>;
  return <Pill tone="danger">{t("campaigns.list.rejected", "Rejected")}</Pill>;
}

export default function CampaignsList() {
  const t = useT();
  useDateTimeVersion(); // sent/scheduled dates re-format live on locale/tz change
  const nav = useNav();
  const [tab, setTab] = useState(() => { const v = (nav.params?.tab as string) || "all"; return APPROVALS_COMING_SOON && v === "approval" ? "all" : v; });
  const [q, setQ] = useState("");
  const [view, setView] = useViewMode("campaigns:view");
  const canApprove = can("campaigns.approve");
  const [allCampaigns, setAllCampaigns] = useState<Campaign[] | null>(null);
  const [counts, setCounts] = useState({ all:0, draft:0, scheduled:0, sent:0, approval:0, automations:0 });
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);
  useEffect(() => {
    listCampaigns({ limit: 500 } as any).then((r: any) => {
      const arr: Campaign[] = (r.campaigns ?? r.data ?? []).map((c: any): Campaign => ({
        id: c.id, name: c.name ?? "", type: c.type === "SMS" ? "SMS" : "Email",
        audience: c.audience ?? "", status: c.status === "sent" ? "sent" : c.status === "scheduled" ? "scheduled" : "draft",
        approval: c.approval ?? c.approvalStatus ?? undefined,
        sentAt: c.sentAt ?? c.sent_at ?? undefined, scheduledAt: c.scheduledAt ?? c.scheduled_at ?? undefined,
        opens: c.opens ?? undefined, clicks: c.clicks ?? undefined, deliveries: c.deliveries ?? c.audienceSize ?? undefined,
        isAutomation: c.isAutomation ?? c.automation ?? false,
      }));
      setAllCampaigns(arr);
      const ct = r.counts ?? {};
      setCounts({ all: ct.all ?? arr.length, draft: ct.draft ?? arr.filter((x) => x.status==="draft").length, scheduled: ct.scheduled ?? arr.filter((x) => x.status==="scheduled").length, sent: ct.sent ?? arr.filter((x) => x.status==="sent").length, approval: ct.approval ?? arr.filter((x) => x.approval==="pending").length, automations: ct.automations ?? arr.filter((x) => (x as any).isAutomation).length });
    }).catch(() => setAllCampaigns([]));
  }, [nonce]);

  // counts loaded from API above

  const filtered = (allCampaigns ?? []).filter((c) => {
    if (tab==="approval" ? c.approval!=="pending" : tab!=="all" && c.status!==tab) return false;
    if (q && !`${c.name} ${c.audience}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  // UIR-26 (Voyage #215): the toolbar Export does real work — one awaited
  // exportCampaign per row in the current filter (N sequential calls, the
  // pushback's sanctioned first cut). Disabled with nothing to export.
  const [exporting, setExporting] = useState(false);
  const exportAll = async () => {
    if (exporting || filtered.length === 0) return;
    setExporting(true);
    let ok = 0;
    for (const c of filtered) { try { await exportCampaign(c.id); ok++; } catch (e) { console.warn("export campaign failed:", c.id, e); } }
    setExporting(false);
    const failed = filtered.length - ok;
    if (failed === 0) toast({ tone: "success", title: t("campaigns.list.exportReady", "Export ready"), subtitle: `${ok} CSV` }); // i18n suggested key: campaigns.list.exportedCountCsvs.icu
    else if (ok === 0) toast({ tone: "danger", title: t("campaigns.list.exportFailed", "Export failed"), subtitle: t("campaigns.list.noCsvWasProducedRetryInAMoment", "No CSV was produced. Retry in a moment.") });
    else toast({ tone: "warn", title: t("campaigns.list.exportPartlyFailed", "Export partly failed"), subtitle: `${ok} / ${filtered.length}` }); // i18n suggested key: campaigns.list.exportedOkOfTotal.icu
  };

  const mkActions = (c: Campaign) => {
    const fail = (verb: string) => (e: unknown) => { console.warn(`${verb} campaign failed:`, e); toast({ tone: "danger", title: i18nT("campaigns.list.couldnTVerbTolowercaseCName.icu", "Couldn't {value0} \"{value1}\"", { value0: verb.toLowerCase(), value1: c.name }), subtitle: t("campaigns.list.nothingChangedRetryInAMoment", "Nothing changed. Retry in a moment.") }); };
    return {
    // (CC, 2026-10-05) campaigns.email / .sms are not registered routes, so open/edit would land on the "being migrated" placeholder: no view/edit action until the composer exists.
    onView: undefined,
    onEdit: undefined,
    onDuplicate: () => duplicateCampaign(c.id).then(() => { toast({ tone:"success", title:i18nT("campaigns.list.duplicatedCName.icu", "Duplicated · {value0}", { value0: c.name }) }); reload(); }).catch(fail("Duplicate")),
    moreHeader: c.name,
    moreItems: [
      ...(!APPROVALS_COMING_SOON && c.approval==="pending" && canApprove ? [
        { icon:<Icon.check size={14}/>, label:t("campaigns.list.approveQueue", "Approve & queue"), onClick:() => approveCampaign(c.id).then(() => { toast({ tone:"success", title:i18nT("campaigns.list.approvedCName.icu", "Approved · {value0}", { value0: c.name }) }); reload(); }).catch(fail("Approve")) },
        { icon:<Icon.x size={14}/>,    label:t("campaigns.list.reject", "Reject"), danger:true, onClick:() => rejectCampaign(c.id, { reason: "Rejected from list" }).then(() => { toast({ tone:"info",    title:i18nT("campaigns.list.rejectedCName.icu", "Rejected · {value0}", { value0: c.name }) }); reload(); }).catch(fail("Reject")) },
        "divider" as const,
      ] : []),
      ...(c.status==="draft" ? [{ icon:<Icon.send size={14}/>,  label:t("campaigns.list.sendNow", "Send now"),         onClick:() => sendCampaign(c.id).then(() => { toast({ tone:"success", title:i18nT("campaigns.list.sentCName.icu", "Sent · {value0}", { value0: c.name }) }); reload(); }).catch(fail("Send")) }] : []),
      ...(c.status==="scheduled" ? [{ icon:<Icon.x size={14}/>, label:t("campaigns.list.cancelSchedule", "Cancel schedule"),    onClick:() => cancelCampaign(c.id).then(() => { toast({ tone:"info", title:t("campaigns.list.scheduleCancelled", "Schedule canceled") }); reload(); }).catch(fail("Cancel")) }] : []),
      { icon:<Icon.download size={14}/>, label:t("campaigns.list.exportAsCsv", "Export CSV"), onClick:() => exportCampaign(c.id).then(() => toast({ tone:"info", title:i18nT("campaigns.list.exportingCNameCsv.icu", "Exporting · {value0}.csv", { value0: c.name }) })).catch(fail("Export")) },
      "divider" as const,
      { icon:<Icon.trash size={14}/>, label:t("common.actions.delete", "Delete"), danger:true, onClick:() => deleteCampaign(c.id).then(() => { toast({ tone:"info", title:i18nT("campaigns.list.deletedCName.icu", "Deleted · {value0}", { value0: c.name }) }); reload(); }).catch(fail("Delete")) },
    ],
  };
  };

  return (
    <Screen rail="campaigns" tabs={[
      { id:"all",       label:t("campaigns.list.all", "All"),               count:counts.all,       onClick:() => setTab("all") },
      { id:"draft",     label:t("campaigns.list.drafts", "Drafts"),            count:counts.draft,     onClick:() => setTab("draft") },
      { id:"scheduled", label:t("common.status.scheduled", "Scheduled"),         count:counts.scheduled, onClick:() => setTab("scheduled") },
      { id:"sent",      label:t("campaigns.list.sent", "Sent"),              count:counts.sent,      onClick:() => setTab("sent") },
      /* Approvals are Coming soon (CC 0224): no "Awaiting approval" tab while the switch is on. */
      ...(APPROVALS_COMING_SOON ? [] : [{ id:"approval",  label:t("campaigns.list.awaitingApproval", "Awaiting approval"), count:counts.approval,  onClick:() => setTab("approval") }]),
      // (CC, 2026-10-05) Automations tab removed: campaigns.notifications is not a registered route.
    ]} activeTab={tab}>
      <PageHeader
        title={t("campaigns.list.campaigns", "Campaigns")}
        subtitle={t("campaigns.list.emailSmsToGuestsSegmentsAndEventAttendees", "Email & SMS to guests, segments, and event attendees")}
        actions={<>
          {!APPROVALS_COMING_SOON && counts.approval > 0 && tab !== "approval" ? (
            /* ui-raw: amber pill that jumps to the approval tab — StatusPill's look with a Button's job; needs either a tonal Button or a clickable StatusPill */
            <button onClick={() => setTab("approval")} style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"7px 12px", borderRadius:999, cursor:"pointer", background:T.orange2, border:`1px solid ${alpha(T.orange1,"40")}`, color:T.orange1, fontFamily:T.font, fontSize:13, fontWeight:600 }}>
              <span style={{ width:7, height:7, borderRadius:999, background:T.orange1 }}/>{counts.approval} {t("campaigns.list.awaitingApproval84e3eb92", "awaiting approval")}</button>
          ) : null}
          <Btn variant="secondary" icon={<Icon.download size={14} />} disabled={exporting || filtered.length === 0} onClick={() => void exportAll()}>{exporting ? t("campaigns.list.exporting", "Exporting…") : t("common.actions.export", "Export")}</Btn>
          {/* (CC, 2026-10-05) the composer route is not registered yet: disabled, not a dead end. */}
          <Btn icon={<Icon.plus />} disabled title={t("chrome.comingSoon.comingSoon", "Coming soon")}>{t("campaigns.list.newCampaign", "New campaign")}</Btn>
        </>}
      />

      <div style={{ padding:"12px 24px", display:"flex", gap:8, alignItems:"center", borderBottom:`1px solid ${T.border}` }}>
        <Input full={false} leading={<Icon.search size={14}/>} placeholder={t("campaigns.list.searchByNameOrAudience", "Search by name or audience…")} style={{ width:320 }} value={q} onChange={(e) => setQ(e.target.value)} />
        <span style={{ fontSize:13, color:T.soft }}>{t("campaigns.list.count.icu", "{count, plural, one {# campaign} other {# campaigns}}", { count: filtered.length })}</span>{/* (CC, 2026-10-05) one ICU plural, was "campaign" + "s" */}
        <span style={{ marginLeft:"auto" }} />
        <ViewToggle mode={view} onChange={setView} />
      </div>

      {!APPROVALS_COMING_SOON && tab==="approval" && counts.approval > 0 ? (
        <div style={{ margin:"12px 24px 0", padding:"10px 14px", background:T.orange2, border:`1px solid ${alpha(T.orange1,"40")}`, borderRadius:8, fontSize:13, color:T.n1, display:"flex", alignItems:"center", gap:10 }}>
          <Icon.info size={14} style={{ color:T.orange1, flexShrink:0 }} />
          <span>{canApprove ? t("campaigns.list.text2", "These campaigns need sign-off before they can send. Approve or reject from the row menu.") : t("campaigns.list.text3", "These campaigns are waiting on an Approver before they can send.")}</span>
        </div>
      ) : null}

      <div style={{ flex:1, overflow:"auto", padding:"12px 24px 24px" }}>
        {filtered.length === 0 ? (
          <EmptyState variant="card" icon={<Icon.send size={20}/>} title={tab==="approval"?t("campaigns.campaignsList.nothingAwaitingApproval", "Nothing awaiting approval"):t("campaigns.campaignsList.noTabAllTabCampaignsYet", "No {value0}campaigns yet", { value0: tab==="all"?"":tab+" " })} body={t("campaigns.list.campaignsYouCreateWillAppearHere", "Campaigns you create will appear here.")}
            action={can("campaigns.write") ? <Btn icon={<Icon.plus size={14}/>} disabled title={t("chrome.comingSoon.comingSoon", "Coming soon")}>{t("campaigns.list.newCampaign", "New campaign")}</Btn> : null}/>
        ) : view === "grid" ? (
          <div style={{ display:"grid", gridTemplateColumns:CARD_GRID, gap:16 }}>
            {filtered.map((c) => (
              <Card key={c.id} style={{ padding:0, display:"flex", flexDirection:"column", overflow:"hidden" }}>
                <div style={{ padding:"16px 18px 14px", flex:1, display:"flex", flexDirection:"column", gap:10 }}>
                  <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                    <span style={{ width:36, height:36, borderRadius:8, background:c.type==="SMS"?alpha(T.green1,"18"):T.p4, color:c.type==="SMS"?T.green1:T.p1, display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      {c.type==="SMS" ? <Icon.msg size={16}/> : <Icon.mail size={16}/>}
                    </span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:T.n1, lineHeight:1.3 }}>{c.name}</div>
                      <div style={{ fontSize:12, color:T.soft, marginTop:3 }}>{c.audience}</div>
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    <CampaignStatus c={c}/><ApprovalPill c={c}/>
                    <Pill>{c.type}</Pill>
                  </div>
                  {c.status==="sent" && c.opens!=null ? (
                    <div style={{ display:"flex", gap:16, fontSize:12, color:T.soft }}>
                      <span><strong style={{ color:T.n1 }}>{c.opens}%</strong> {t("campaigns.list.opened", "opened")}</span>
                      <span><strong style={{ color:T.n1 }}>{c.clicks}%</strong> {t("campaigns.list.clicked", "clicked")}</span>
                      <span><strong style={{ color:T.n1 }}>{c.deliveries}</strong> {t("campaigns.list.delivered", "delivered")}</span>
                    </div>
                  ) : null}
                  {c.scheduledAt ? <div style={{ fontSize:12, color:T.soft }}>{t("campaigns.list.scheduled", "Scheduled ·")}{" "}{friendlyDateTime(c.scheduledAt)}</div> : null}
                </div>
                <CardActionBar {...mkActions(c)}/>
              </Card>
            ))}
          </div>
        ) : (
          <Card style={{ padding:0 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:T.font, fontSize:13 }}>
              <thead>
                <tr style={{ background:T.n6 }}>
                  <th style={thStyle}>{t("campaigns.list.campaign", "Campaign")}</th>
                  <th style={thStyle}>{t("campaigns.list.type", "Type")}</th>
                  <th style={thStyle}>{t("campaigns.list.status", "Status")}</th>
                  <th style={thStyle}>{t("campaigns.list.audience", "Audience")}</th>
                  <th style={thStyle}>{t("campaigns.list.sentScheduled", "Sent / Scheduled")}</th>
                  <th style={{ ...thStyle, width:80 }}>{t("campaigns.list.opens", "Opens")}</th>
                  <th style={{ ...thStyle, width:130, textAlign:"right" as const }}>{t("campaigns.list.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c,i) => (
                  <tr key={c.id} style={{ borderTop:i?`1px solid ${T.border}`:"none" }}>
                    <td style={tdStyle}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ width:28, height:28, borderRadius:6, background:c.type==="SMS"?alpha(T.green1,"18"):T.p4, color:c.type==="SMS"?T.green1:T.p1, display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {c.type==="SMS"?<Icon.msg size={13}/>:<Icon.mail size={13}/>}
                        </span>
                        <div>
                          <div style={{ color:T.n1, fontWeight:600 }}>{c.name}</div>
                          <div style={{ color:T.soft, fontSize:12 }}>{c.audience}</div>
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}><Pill>{c.type}</Pill></td>
                    <td style={tdStyle}><div style={{ display:"flex", gap:4 }}><CampaignStatus c={c}/><ApprovalPill c={c}/></div></td>
                    <td style={{ ...tdStyle, color:T.n2, fontSize:12 }}>{c.audience}</td>
                    <td style={{ ...tdStyle, color:T.soft, fontSize:12 }}>{c.sentAt || c.scheduledAt ? friendlyDateTime(c.sentAt || c.scheduledAt) : "—"}</td>
                    <td style={{ ...tdStyle, color:T.n1, fontVariantNumeric:"tabular-nums" }}>{c.opens!=null?t("campaigns.list.text5", "{opens}%", { opens: c.opens }):"—"}</td>
                    <td style={tdStyle} onClick={(e)=>e.stopPropagation()}><RowActions {...mkActions(c)}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
        <ExplainerFooter>
          <b>{t("campaigns.list.campaigns", "Campaigns")}</b> {t("campaigns.list.areTargetedEmailOrSmsSendsToAGuestListSegmentOr", "are targeted email or SMS sends to a guest list, segment, or event audience.")}</ExplainerFooter>
      </div>
    </Screen>
  );
}
